/**
 * PROP LOD helpers (2026-11-04, render-cost pass) — pure geometry, no DOM, so
 * `agent/bench-prop-lod.mjs` runs them on the real scanned-rock assets offline.
 *
 *  - `parseLodBin`: the LOW-DETAIL rock built OFFLINE by
 *    agent/tools/build-rock-lod.mjs (meshoptimizer simplifier, ~20 % of the
 *    scan's triangles, seam-aware, no flipped faces) for RUBBLE: the canyon's
 *    ~580 talus stones were each a full ~5k-tri scan, 3 M main + 3 M shadow
 *    triangles per frame. (A runtime SimplifyModifier was tried first: it
 *    flipped 6-18 % of the faces — stones rendered inside-out/white.)
 *  - `hullProxy`: a low-poly convex hull that CASTS the shadow for a scan.
 */
import * as THREE from "three";
import { ConvexGeometry } from "three/examples/jsm/geometries/ConvexGeometry.js";

const LOD_MAGIC = 0x314c504e; // 'NPL1'

/** Decode a `.lod.bin` (format: see build-rock-lod.mjs) into a geometry in the
 *  GLB's accessor space. Null on any malformed input. */
export function parseLodBin(buf: ArrayBuffer): THREE.BufferGeometry | null {
  if (buf.byteLength < 12) return null;
  const head = new Uint32Array(buf, 0, 3);
  const [magic, vc, ic] = [head[0], head[1], head[2]];
  if (magic !== LOD_MAGIC || vc < 3 || ic < 3 || ic % 3 !== 0) return null;
  const wide = vc >= 65536;
  const need = 12 + vc * 8 * 4 + ic * (wide ? 4 : 2);
  if (buf.byteLength < need) return null;
  let o = 12;
  const pos = new Float32Array(buf.slice(o, o + vc * 12)); o += vc * 12;
  const nor = new Float32Array(buf.slice(o, o + vc * 12)); o += vc * 12;
  const uv = new Float32Array(buf.slice(o, o + vc * 8)); o += vc * 8;
  const idx = wide ? new Uint32Array(buf.slice(o, o + ic * 4)) : new Uint16Array(buf.slice(o, o + ic * 2));
  for (let i = 0; i < idx.length; i++) if (idx[i] >= vc) return null;
  for (let i = 0; i < pos.length; i++) if (!Number.isFinite(pos[i])) return null;
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  g.setAttribute("normal", new THREE.BufferAttribute(nor, 3));
  g.setAttribute("uv", new THREE.BufferAttribute(uv, 2));
  g.setIndex(new THREE.BufferAttribute(idx, 1));
  return g;
}

/** Fraction of triangles whose winding normal points AGAINST the average of
 *  their vertex normals (≈0 for a clean mesh; a flipped face is back-face
 *  culled and the stone shows its inside). */
export function flippedFraction(g: THREE.BufferGeometry): number {
  const p = g.getAttribute("position"), n = g.getAttribute("normal");
  const idx = g.index;
  const tc = idx ? idx.count / 3 : p.count / 3;
  const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3(), e = new THREE.Vector3(), fn = new THREE.Vector3();
  let bad = 0, counted = 0;
  for (let t = 0; t < tc; t++) {
    const i0 = idx ? idx.getX(t * 3) : t * 3, i1 = idx ? idx.getX(t * 3 + 1) : t * 3 + 1, i2 = idx ? idx.getX(t * 3 + 2) : t * 3 + 2;
    a.fromBufferAttribute(p, i0); b.fromBufferAttribute(p, i1); c.fromBufferAttribute(p, i2);
    fn.subVectors(c, b).cross(e.subVectors(a, b));
    if (fn.lengthSq() < 1e-14) continue;
    const vx = n.getX(i0) + n.getX(i1) + n.getX(i2), vy = n.getY(i0) + n.getY(i1) + n.getY(i2), vz = n.getZ(i0) + n.getZ(i1) + n.getZ(i2);
    counted++;
    if (fn.x * vx + fn.y * vy + fn.z * vz < 0) bad++;
  }
  return counted ? bad / counted : 1;
}

/** Convex hull of ~`samples` well-spread vertices, shrunk by `shrink` about
 *  its box centre so it stays inside the real surface. A hull has ~2
 *  triangles per point on it, so 140 samples ≈ 280 triangles. */
export function hullProxy(src: THREE.BufferGeometry, samples = 140, shrink = 0.95): THREE.BufferGeometry {
  const pos = src.getAttribute("position");
  const pts: THREE.Vector3[] = [];
  const step = Math.max(1, Math.floor(pos.count / samples));
  for (let i = 0; i < pos.count; i += step) pts.push(new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i)));
  const hull = new ConvexGeometry(pts);
  hull.computeBoundingBox();
  const c = hull.boundingBox!.getCenter(new THREE.Vector3());
  hull.translate(-c.x, -c.y, -c.z);
  hull.scale(shrink, shrink, shrink);
  hull.translate(c.x, c.y, c.z);
  hull.deleteAttribute("uv");
  hull.computeBoundingSphere();
  return hull;
}
